import { Icons } from "assets/icons";
import FilterBar from "components/common/FilterBar";
import NoDataFound from "components/common/NoDataFound";
import { StatusBadge } from "components/common/StatusBadge";
import Switch from "components/common/Switch";
import TableShimmer from "components/common/TableShimmer";
import { handleStatusUpdate } from "helpers/apiUtils";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Modal, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import {
  getPlans,
  resetPlanFilters,
  setPlanFilter,
  updatePlanStatus,
} from "store/slices/membershipSlice";
import { showDeleteModal } from "store/slices/modalSlice";
import {
  ORDER_BY_OPTIONS,
  PLAN_OPTIONS,
  STATUS_OPTIONS,
} from "utils/constants";
import { getActionFlags } from "utils/getActionFlags";
import { formatUTCDate, getMinMaxDate } from "utils/utils";
import FreeWash from "../FreeWash";
import Paginations from "components/common/Paginations";
import { usePermission } from "hooks/usePermission";
import PageHeader from "components/common/PageHeader";

const Plans = () => {
  const { t } = useTranslation();
  const { trash, edit, noPlans, view } = Icons || {};
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { canAction } = usePermission();
  const { canView, canEdit, canDelete, canAdd } = canAction("membership_plan");
  const { canView: canFreeWashView } = canAction("freewash_rule");

  // Split selectors to prevent unnecessary re-renders
  const loading = useSelector((state) => state.Membership.loading);
  const isLoading = useSelector((state) => state.Membership.isLoading);
  const plans = useSelector((state) => state.Membership.plans);
  const loadingItemId = useSelector((state) => state.Membership.loadingItemId);
  const totalPages = useSelector((state) => state.Membership.totalPages);
  const currentPage = useSelector((state) => state.Membership.currentPage);
  const totalCount = useSelector((state) => state.Membership.totalCount);
  const planFilters = useSelector(
    (state) => state.Membership.planFilters,
    shallowEqual
  );

  const { perPage, pageNo, status, orderBy, keyword, dates, planType } =
    planFilters;

  const [freeWashModal, setFreeWashModal] = useState(false);

  useEffect(() => {
    if (canView) {
      getAllData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView]);

  const getAllData = useCallback(
    (page, action) => {
      const { isReset, isResetKeyword } = getActionFlags(action);

      const payload = {
        perPage,
        pageNo: page || pageNo,
        status: isReset ? -1 : status,
        orderBy: isReset ? -1 : orderBy,
        planType: isReset ? "" : planType,
        keyword: isResetKeyword ? "" : keyword || "",
        dateRange: isReset ? {} : getMinMaxDate(dates),
      };

      dispatch(getPlans(payload));
    },
    [perPage, pageNo, status, orderBy, planType, keyword, dates, dispatch]
  );

  const handleReset = useCallback(() => {
    dispatch(resetPlanFilters());
    getAllData(1, { actionType: "RESET" });
  }, [dispatch, getAllData]);

  const handleModalClose = useCallback(() => {
    setFreeWashModal(false);
  }, []);

  const handleModalSuccess = useCallback(() => {
    setFreeWashModal(false);
  }, []);

  const filtersList = useMemo(
    () => [
      {
        key: "status",
        placeholder: t("Status"),
        type: "singleSelect",
        options: STATUS_OPTIONS,
      },
      {
        key: "planType",
        placeholder: t("Plan Type"),
        type: "singleSelect",
        options: PLAN_OPTIONS,
      },
      {
        key: "orderBy",
        placeholder: t("Order By"),
        type: "singleSelect",
        options: ORDER_BY_OPTIONS,
      },
    ],
    [t]
  );

  if (!canView) return null;

  return (
    <div className="page-content addons_page">
      <div className="g_between">
        <PageHeader title={t("Membership Plans")} />

        {canFreeWashView && (
          <button className="main_btn" onClick={() => setFreeWashModal(true)}>
            {t("Manage Free Wash")}
          </button>
        )}
      </div>

      <FilterBar
        keyword={keyword}
        setKeyword={(value) =>
          dispatch(setPlanFilter({ key: "keyword", value }))
        }
        keywordPlaceholder={t("Search by Title")}
        filters={filtersList}
        selectedFilters={{ keyword, status, orderBy, planType }}
        onFilterUpdate={(key, value) => dispatch(setPlanFilter({ key, value }))}
        dates={dates}
        setDates={(value) => dispatch(setPlanFilter({ key: "dates", value }))}
        onSearch={getAllData}
        onReset={handleReset}
        isBtn={canAdd}
        btnText={t("Add Plan")}
        onBtnClick={() => navigate("/membership/plans/add-plan")}
      />

      {loading && <TableShimmer rows={12} columns={8} />}

      {!loading && plans?.length === 0 && (
        <NoDataFound title={t("No plans found")} icon={noPlans} />
      )}

      {!loading && plans?.length > 0 && (
        <div className="common_table_style">
          <Table hover responsive>
            <thead className="t_head">
              <tr>
                <th style={{ width: 70 }}>{t("S.No")}</th>
                <th>{t("Title")}</th>
                <th>{t("Highlight Title")}</th>
                <th>{t("Plan Type")}</th>
                <th>{t("Created Date")}</th>
                <th>{t("Status")}</th>
                <th>{t("Actions")}</th>
              </tr>
            </thead>
            <tbody>
              {plans?.length > 0 ? (
                plans.map((addon, index) => {
                  const { _id, title, subTitle, planType, status, createdAt } =
                    addon || {};
                  return (
                    <tr key={_id}>
                      <td>{index + 1}</td>

                      <td>
                        <div className="">
                          <Link to={`/membership/plans/plan-details?id=${_id}`}>
                            {title?.en}
                          </Link>
                        </div>
                        {title?.ar && (
                          <div className="text-muted small">{title?.ar}</div>
                        )}
                      </td>
                      <td>
                        <div className="">{subTitle?.en}</div>
                        {subTitle?.ar && (
                          <div className="text-muted small">{subTitle?.ar}</div>
                        )}
                      </td>

                      <td>
                        {
                          PLAN_OPTIONS?.find(
                            (option) => option.value === planType
                          )?.name
                        }
                      </td>

                      <td>
                        <div className="ltr_text">
                          {formatUTCDate(createdAt)}
                        </div>
                      </td>
                      <td>
                        <StatusBadge status={status} t={t} />
                      </td>

                      <td>
                        <div className="actions">
                          {canEdit && (
                            <>
                              <Switch
                                checked={status === 1}
                                handleOnChange={() => {
                                  handleStatusUpdate({
                                    _id,
                                    status,
                                    dispatch,
                                    action: updatePlanStatus,
                                  });
                                }}
                                disabled={isLoading && loadingItemId === _id}
                              />
                              <img
                                src={edit}
                                width={20}
                                height={20}
                                alt="edit"
                                onClick={() =>
                                  navigate(
                                    `/membership/plans/edit-plan?id=${_id}`
                                  )
                                }
                              />
                            </>
                          )}
                          {canView && (
                            <Link
                              to={`/membership/plans/plan-details?id=${_id}`}
                            >
                              <img
                                src={view}
                                width={20}
                                height={20}
                                alt="view"
                              />
                            </Link>
                          )}
                          {canDelete && (
                            <img
                              src={trash}
                              width={20}
                              height={20}
                              alt="delete"
                              onClick={() =>
                                dispatch(
                                  showDeleteModal({
                                    title: t("Delete Plan"),
                                    message: t(
                                      "Are you sure you want to delete this Plan?"
                                    ),
                                    itemId: _id,
                                    actionType: "PLAN",
                                  })
                                )
                              }
                            />
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={7} className="text-center py-3 text-muted">
                    {t("No plans found")}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      )}

      {!loading && (
        <Paginations
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={totalCount}
          onPageChange={getAllData}
        />
      )}

      {freeWashModal && (
        <Modal
          show={freeWashModal}
          onHide={handleModalClose}
          centered
          className="common_modal w_876"
        >
          <h2 className="modal_title">{t("Manage Free Wash")}</h2>
          <div className="content">
            <FreeWash
              onCancel={handleModalClose}
              onSuccess={handleModalSuccess}
            />
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Plans;